// ============================================================
// 动态路由 store
// 负责：拉 menus → 拆成侧栏树 + 叶子页 → 挂到同一条 Layout
// ============================================================
import { defineStore } from "pinia";
import type { RouteRecordRaw, RouteComponent } from "vue-router";
import type { UserButtonPage, UserMenuItem } from "@shared/types/user";
import type { SidebarMenuItem } from "@shared/types/sidebarMenu";
import { buildLayoutRoute, splitUserMenus } from "@shared/router/mapUserMenus";
import { APP_LAYOUT_ROUTE_NAME } from "@shared/constants/menu";

interface PermissionState {
    loaded: boolean;
    /** 已注入的顶层 route name（现在只有 AppLayout），登出时 removeRoute */
    routeNames: string[];
    /** 侧栏菜单树 */
    sidebarMenus: SidebarMenuItem[];
    /** Layout 直属业务页（affix / 调试） */
    pageRoutes: RouteRecordRaw[];
}

export interface GenerateRoutesOptions {
    layout: RouteComponent;
    fetchUserMenus: () => Promise<UserMenuItem[] | { menus: UserMenuItem[]; buttonPages?: UserButtonPage[] }>;
}

export const usePermissionStore = defineStore("permission", {
    state: (): PermissionState => ({
        loaded: false,
        routeNames: [],
        sidebarMenus: [],
        pageRoutes: [],
    }),

    actions: {
        applyMenus(menus: UserMenuItem[], layout: RouteComponent, buttonPages: UserButtonPage[] = []): RouteRecordRaw[] {
            const { sidebar, pages } = splitUserMenus(menus, buttonPages);
            const layoutRoute = buildLayoutRoute(pages, layout);
            this.sidebarMenus = sidebar;
            this.pageRoutes = pages;
            this.routeNames = [APP_LAYOUT_ROUTE_NAME];
            this.loaded = true;
            return [layoutRoute];
        },

        async generateRoutes(opts: GenerateRoutesOptions): Promise<RouteRecordRaw[]> {
            const raw = await opts.fetchUserMenus();
            const menus = Array.isArray(raw) ? raw : (raw.menus ?? []);
            const buttonPages = Array.isArray(raw) ? [] : (raw.buttonPages ?? []);
            return this.applyMenus(menus, opts.layout, buttonPages);
        },

        /** 登出时调用，外部再 router.removeRoute */
        reset(): string[] {
            const names = [...this.routeNames];
            this.loaded = false;
            this.routeNames = [];
            this.sidebarMenus = [];
            this.pageRoutes = [];
            return names;
        },
    },
});
